import {Card} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {isEmpty, isNil, isNotNil, mean} from 'ramda';

import type {AbcXyzAnalysisDetailResponse} from '@dms/api/reporting';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useAbcXyzChartColors} from '../hooks/useAbcXyzChartColors';
import type {AbcXyzLegendItem} from '../types/AbcXyzLegendItem';
import {AbcXyzChartLegend} from './AbcXyzChartLegend';

const CHART_HEIGHT_STEPS = 28;
const Y_AXIS_WIDTH = 60;
const QUANTITY_DECIMALS = 2;
const AXIS_TICK_DECIMALS = 0;
/** Length of the `YYYY-MM` prefix of an ISO date, which is all the month axis needs. */
const MONTH_LABEL_LENGTH = 7;

interface AbcXyzIssuedQuantityChartProps extends RequiredTestIdProps {
  detail: AbcXyzAnalysisDetailResponse;
  title: string;
  /** Stretch to the height of the surrounding row instead of using the fixed chart height. */
  isFullHeight?: boolean;
}

export function AbcXyzIssuedQuantityChart(props: AbcXyzIssuedQuantityChartProps) {
  const formatNumber = useFormatNumber();
  const colors = useAbcXyzChartColors();

  // The API sends every closed month of the window, zero months included, so the bars are drawn
  // as received; nothing is filled in or reordered here.
  const data = (props.detail.monthlyHistory ?? []).map((month) => ({
    month: month.month?.slice(0, MONTH_LABEL_LENGTH) ?? EMPTY_PLACEHOLDER,
    issuedQuantity: month.issuedQuantity,
  }));
  const unit = props.detail.unitName ?? EMPTY_PLACEHOLDER;
  // The dashed line is the mean of the plotted bars, not the XYZ `meanMonthlyQuantity`, which may
  // be based on another quantity (returns, outliers) and would not match what the bars show.
  const issuedQuantities = data.map((month) => month.issuedQuantity).filter(isNotNil);
  const meanLines = isEmpty(issuedQuantities) ? [] : [mean(issuedQuantities)];

  const legendItems: AbcXyzLegendItem[] = [
    {
      id: 'issuedQuantity',
      label: i18n.t('entity.warehouse.reporting.issuedQuantity'),
      color: colors.primary,
      shape: 'bar',
    },
    ...meanLines.map((meanValue) => ({
      id: 'mean',
      label: `${i18n.t('entity.warehouse.reporting.mean')} (${formatNumber(meanValue, QUANTITY_DECIMALS) ?? EMPTY_PLACEHOLDER})`,
      color: colors.highlight,
      shape: 'dashedLine' as const,
    })),
  ];

  return (
    <Card isFullHeight={props.isFullHeight} data-testid={props['data-testid']}>
      <VStack spacing={2} height="100%">
        <Text size="small" alternative>
          {props.title}
        </Text>
        <Show when={!isEmpty(data)}>
          <AbcXyzChartLegend items={legendItems} data-testid={suffixTestId('legend', props)} />
        </Show>
        {isEmpty(data) ? (
          <Text color="secondary" data-testid={suffixTestId('empty', props)}>
            {i18n.t('entity.warehouse.reporting.noIssueHistory')}
          </Text>
        ) : (
          <Box
            width="100%"
            height={props.isFullHeight ? undefined : CHART_HEIGHT_STEPS}
            flex={props.isFullHeight ? 1 : undefined}
            minHeight={0}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis
                  width={Y_AXIS_WIDTH}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => formatNumber(value, AXIS_TICK_DECIMALS) ?? ''}
                />
                <Tooltip
                  formatter={(value: number) =>
                    isNil(value)
                      ? EMPTY_PLACEHOLDER
                      : `${formatNumber(value, QUANTITY_DECIMALS) ?? EMPTY_PLACEHOLDER} ${unit}`
                  }
                />
                {/* An array rather than a `Show`: recharts only picks up its own element types as
                  direct children, so a wrapper component would hide the line from the chart. */}
                {meanLines.map((meanValue) => (
                  <ReferenceLine
                    key="mean"
                    y={meanValue}
                    stroke={colors.highlight}
                    strokeDasharray="4 4"
                  />
                ))}
                <Bar
                  dataKey="issuedQuantity"
                  name={i18n.t('entity.warehouse.reporting.issuedQuantity')}
                  fill={colors.primary}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </VStack>
    </Card>
  );
}
